import {useEffect, useRef} from 'react';
import {useSearchParams} from '../../../router/compat';
import {useAuth} from '../../auth/context/AuthContext';
import {getDefaultFilters} from '../constants/defaultFilters';
import {readStoredFilters} from '../utils/ticketFilterStorage';

const FILTER_PARAMS = ['status', 'priority', 'assignee_id'];

export function useDefaultFilters(): void {
	const [searchParams, setSearchParams] = useSearchParams();
	const {status, user} = useAuth();
	const hasRunRef = useRef(false);

	useEffect(() => {
		if (hasRunRef.current) return;
		if (status !== 'authenticated' || !user) return;

		hasRunRef.current = true;

		const hasFilterParams = FILTER_PARAMS.some(p => searchParams.has(p));
		if (hasFilterParams) return;

		if (searchParams.has('view')) return;

		const next = new URLSearchParams(searchParams);
		const stored = readStoredFilters(user.id);

		if (stored) {
			// The stored blob covers more keys than FILTER_PARAMS guards, so a link
			// carrying e.g. `sla_status` reaches here. An explicit URL value still wins
			// for its own key — appending blindly would duplicate it in the query.
			for (const [k, v] of stored) {
				if (searchParams.has(k)) continue;
				next.append(k, v);
			}
		} else {
			const defaults = getDefaultFilters(user.role, user.id);
			defaults.status.forEach(v => next.append('status', v));
			defaults.priority.forEach(v => next.append('priority', v));
			defaults.assignee_id.forEach(v => next.append('assignee_id', v));
		}

		setSearchParams(next, {replace: true});
	}, [status, user, searchParams, setSearchParams]);
}
